import {
  Box,
  Container,
  Stack,
  Text,
  Link,
  IconButton,
  SimpleGrid,
  Flex,
  GridItem,
  Separator,
  Spacer,
} from '@chakra-ui/react';
import { FaGithub, FaInstagram, FaYoutube, FaTiktok } from 'react-icons/fa';
import { NavLink } from 'react-router-dom';
import { routeTo } from '@/router/routeTo';
import { LanguageSelector } from '../common/LanguageSelector';
import { useTranslation } from 'react-i18next';
import { AppConfig } from '@/config/app';
import { LogoWordmark } from '../icons/Logo';

export const PublicFooter = () => {
  const { t } = useTranslation(["public"]);

  return (
    <Box
      as="footer"
    >
      <Container as={Stack} maxW={'container.lg'} py={10}>
        {/*
          This is the main grid for the footer links.Kreoza
          - 1 column on mobile (base)
          - 2 columns on small screens (sm)
          - 4 columns on medium screens and up (md)
        */}
        <SimpleGrid columns={{ base: 1, sm: 2, md: 4 }} gap={8}>
          {/* Column 1: Brand Info & Mission */}
          <GridItem
            display="flex"
            justifyContent="center"
          >
            <Stack gap={6} align={{ base: 'center', md: 'flex-start' }}>
              <LogoWordmark />
              <Text fontSize={'sm'} textAlign={{ base: 'center', md: 'left' }}>
                {t('public:footer.mission')}
              </Text>
            </Stack>
          </GridItem>

          {/* Column 2: App Links */}
          <GridItem
            display="flex"
            justifyContent="center"
          >
            <Stack align="center" gap={3}>
              {/* Title */}
              <Text fontWeight="bold" textAlign="center">
                {AppConfig.name}
              </Text>

              {/* Links */}
              <Stack gap={4} align="flex-start">
                <NavLink to={routeTo.contact()}>
                  {t('public:contact.shortTitle')}
                </NavLink>
                <NavLink to={routeTo.support()}>
                  {t('public:support.shortTitle')}
                </NavLink>
                <NavLink to={routeTo.privacy()}>
                  {t('public:privacy.shortTitle')}
                </NavLink>
                <NavLink to={routeTo.terms()}>
                  {t('public:terms.shortTitle')}
                </NavLink>
              </Stack>
            </Stack>
          </GridItem>

          {/* Column 3: Resource Links */}
          <GridItem
            display="flex"
            justifyContent="center"
          >
            <Stack align="center" gap={3}>
              {/* Title */}
              <Text fontWeight={'bold'}>{t('public:resources.title')}</Text>
              <Stack
                align={{ base: 'center', md: 'flex-start' }}
                gap={3}
              >
                <NavLink to={routeTo.faq()}>{t('public:faq.shortTitle')}</NavLink>
                <NavLink to={routeTo.help()}>{t('public:help.shortTitle')}</NavLink>
                <NavLink to={routeTo.blog()}>{t('public:blog.shortTitle')}</NavLink>
                <NavLink to={routeTo.aboutUs()}>{t('public:aboutUs.shortTitle')}</NavLink>
              </Stack>
            </Stack>
          </GridItem>

          {/* Column 4: Language Selector */}
          <GridItem
            display="flex"
            justifyContent="center"
          >
            <Stack
              align={{ base: 'center', md: 'flex-start' }}
              gap={4}
            >
              <Text fontWeight={'bold'}>{t('public:connect.title')}</Text>
              <Stack direction={'row'} gap={3}>
                <Link href={AppConfig.links.youtube} aria-label='YouTube'>
                  <IconButton
                    aria-label='YouTube'
                    variant="ghost"
                    fontSize="20px"
                  >
                    <FaYoutube />
                  </IconButton>
                </Link>
                <Link href={AppConfig.links.tiktok} aria-label='TikTok'>
                  <IconButton
                    aria-label='TikTok'
                    variant="ghost"
                    fontSize="20px"
                  >
                    <FaTiktok />
                  </IconButton>
                </Link>
                <Link href={AppConfig.links.github} aria-label='GitHub'>
                  <IconButton
                    aria-label='GitHub'
                    variant="ghost"
                    fontSize="20px"
                  >
                    <FaGithub />
                  </IconButton>
                </Link>
                <Link href={AppConfig.links.instagram} aria-label='Instagram'>
                  <IconButton
                    aria-label='Instagram'
                    variant="ghost"
                    fontSize="20px"
                  >
                    <FaInstagram />
                  </IconButton>
                </Link>
              </Stack>
            </Stack>
          </GridItem>
        </SimpleGrid>

        {/* This Divider adds a clean line separating the top and bottom of the footer */}
        <Separator my={8} borderColor="gray.300" _dark={{ borderColor: 'gray.700' }} />

        {/* Bottom bar for copyright notice */}
        <Container>
          <Flex>
            <Text fontSize="sm" textAlign="center">
              {t("footer.rights", {
                year: new Date().getFullYear(),
                appName: AppConfig.name
              })}
            </Text>

            <Spacer />

            <LanguageSelector popoverPlacement="bottom-end" />
          </Flex>
        </Container>
      </Container>
    </Box>
  );
};